import React, { useState } from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";

// ─── Testimonial data ─────────────────────────────────────────────────────────
// `photo` is optional — add a path like "/images/gavin-fletcher.jpg" if you
// manually save their LinkedIn headshot. Falls back to initials avatar.
const TESTIMONIALS = [
  {
    id: "gavin",
    name: "Gavin Fletcher",
    title: "Driving adoption of the first AI copilot for sustainability",
    meta: "Senior to Sam at UMA · Sep 2021",
    photo: null,
    initials: "GF",
    color: "#0066FF",
    quote:
      "Sam joined the Founding Team at UMA at a critical stage in the growth journey as Head of Design. His depth of expertise in user experience was evident from day one. Combined with his enthusiasm and 'can-do' attitude, his human-centred approach to interface design contributed significantly to a step-change in the maturity of UMA, across mobile and web. I have no hesitation in recommending Sam.",
  },
  {
    id: "stephanie",
    name: "Stephanie Hague-Evans",
    title: "Founder, HEvans People Services · ILM Level 7 Coach",
    meta: "Senior to Sam · Oct 2020",
    photo: null,
    initials: "SH",
    color: "#A855F7",
    quote:
      "Sam is personable, hard-working, and professional. His technical abilities, creative insights, and UX know-how tangibly elevated the online booking journey for our customers. He made some great improvements to our sites and actively engaged the wider team with A/B testing and inclusive feedback surveys. He was an impressive addition to our team — he would be an asset to any business.",
  },
  {
    id: "rooz",
    name: "Rooz Golpa",
    title: "Artist & Researcher",
    meta: "Colleague · Jul 2020",
    photo: null,
    initials: "RG",
    color: "#00D4AA",
    quote:
      "I highly recommend Sam for anything and everything that relates to web design and creative digital work. Sam and his services offer exclusivity and bright thinking. I have had the pleasure to work with him and have always been satisfied with the results.",
  },
  {
    id: "kashan",
    name: "Kashan Qamar",
    title: "Senior Solutions Architect",
    meta: "Client · Jul 2020",
    photo: null,
    initials: "KQ",
    color: "#F59E0B",
    quote:
      "Sam is a great person to work with. He has a clear vision of what the market needs.",
  },
];

const LINKEDIN_URL =
  "https://www.linkedin.com/in/samerpik/details/recommendations/";

// ─── Avatar ───────────────────────────────────────────────────────────────────
function Avatar({ t }) {
  if (t.photo) {
    return (
      <img
        src={t.photo}
        alt={t.name}
        className="w-11 h-11 rounded-full object-cover"
        style={{ border: `2px solid ${t.color}33` }}
      />
    );
  }
  return (
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 font-mono text-[13px] font-semibold select-none"
      style={{
        background: `${t.color}18`,
        border: `2px solid ${t.color}40`,
        color: t.color,
      }}
    >
      {t.initials}
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
function TestimonialCard({ t, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.a
      href={LINKEDIN_URL}
      target="_blank"
      rel="noreferrer"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.75, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative flex flex-col rounded-[20px] border bg-white p-8 md:p-10 no-underline"
      style={{
        borderColor: hovered ? `${t.color}40` : "rgba(0,0,0,0.07)",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        boxShadow: hovered
          ? `0 16px 48px -12px ${t.color}22, 0 0 0 1px ${t.color}20`
          : "0 2px 16px -4px rgba(0,0,0,0.06)",
      }}
      data-cursor="magnetic"
    >
      {/* Decorative quote mark */}
      <div
        className="absolute top-7 right-8 font-serif text-[72px] leading-none select-none pointer-events-none"
        style={{ color: `${t.color}14`, lineHeight: 1 }}
        aria-hidden="true"
      >
        "
      </div>

      {/* Quote */}
      <blockquote className="flex-1 text-[15px] md:text-[16px] leading-[1.72] text-ink/75 mb-8">
        "{t.quote}"
      </blockquote>

      {/* Footer — avatar + name + LinkedIn badge */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar t={t} />
          <div className="min-w-0">
            <div className="font-sans text-[14px] font-semibold text-ink leading-tight truncate">
              {t.name}
            </div>
            <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-mute leading-tight mt-0.5 truncate">
              {t.meta}
            </div>
          </div>
        </div>

        {/* LinkedIn badge */}
        <div
          className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all duration-200"
          style={{
            background: hovered ? "#0A66C218" : "#0A66C20D",
            color: "#0A66C2",
          }}
        >
          <Linkedin size={12} strokeWidth={2} />
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase font-semibold">
            LinkedIn
          </span>
        </div>
      </div>

      {/* Bottom accent line */}
      <div
        className="absolute bottom-0 left-0 h-[2px] rounded-b-[20px] transition-all duration-500"
        style={{
          background: `linear-gradient(90deg, ${t.color}, transparent)`,
          width: hovered ? "60%" : "0%",
        }}
      />
    </motion.a>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────
export default function Testimonials() {
  return (
    <section
      id="testimonials"
      className="chapter relative py-24 md:py-36"
      data-testid="chapter-testimonials"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <div>
            <div className="label-mono mb-6">— Recommendations</div>
            <h2 className="font-serif text-[44px] md:text-[68px] leading-[1.02] tracking-tight text-ink">
              What people{" "}
              <span className="italic text-mute">actually say.</span>
            </h2>
          </div>

          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#0A66C2]/25 bg-[#0A66C2]/6 hover:bg-[#0A66C2]/12 transition-colors"
            data-cursor="magnetic"
          >
            <Linkedin size={15} strokeWidth={2} className="text-[#0A66C2]" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-semibold text-[#0A66C2]">
              View all on LinkedIn
            </span>
          </a>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={t.id} t={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
